import React, { useState } from 'react';
import styled from 'styled-components';
import Colors from '../../../../ui/global/Colors';
import Fonts from '../../../../ui/global/Fonts';
import LoadingSpinner from '../../../../ui/components/LoadingSpinner';
import LoadingDots from '../../../../ui/components/LoadingDots';

const ArtAssetContainer = styled.div<{ $isDisabled?: boolean; $isSelected?: boolean; $width?: string }>`
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: flex-start;
    width: ${props => props.$width || '100%'};
    border-radius: 4px;
    background-color: ${Colors.transparent};
    border: 1px solid ${Colors.grey8};
    font-size: 14px;
    line-height: 1.2em;
    padding: 5px;
    gap: 6px;
    user-select: none;
    opacity: ${props => props.$isDisabled ? 0.4 : 1};
    cursor: ${props => props.$isDisabled ? 'default' : 'pointer'};

    ${props => !props.$isDisabled ? `
        &:hover {
            border: 1px solid ${Colors.grey7};
        }

        &:active {
            // background-color: ${Colors.black};
            border: 1px solid ${Colors.grey5};
        }
    ` : ''}

    ${props => !props.$isDisabled && props.$isSelected && `
        &:hover {
            background-color: ${Colors.grey8};
        }

        background-color: ${Colors.grey8};
    `}
`;

const Image = styled.img`
    position: relative;
    width: 100%;
    height: 100%;
    border-radius: 2px;
    object-fit: contain;
    user-select: none;
    aspect-ratio: 1 / 1;
    max-width: 100%;
    -webkit-user-drag: none;
    -khtml-user-drag: none;
    -moz-user-drag: none;
    -o-user-drag: none;
    user-drag: none;
`;

const Blank = styled.div`
    position: relative;
    width: 100%;
    height: 100%;
    background-color: ${Colors.black};
    border-radius: 2px;
    user-select: none;
    aspect-ratio: 1 / 1;
    max-width: 100%;
`;

const Name = styled.h4`
    width: 100%;
    font-family: ${Fonts.lexendSemiLight.fontFamily};
    font-weight: ${Fonts.lexendSemiLight.fontWeight};
    color: ${Colors.grey1};
    text-align: center;
    padding: 2px 2px 6px 2px;
    margin: 0 auto;
`;

const ButtonContainer = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    padding: 4px;
    gap: 10px;
    z-index: 10;
`;

const LoadingContainer = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    // background-color: rgba(0, 0, 0, 0.2);
    border-radius: 2px;
`;

interface ArtAssetProps {
    art: {
        name: string;
        image: string;
    };
    buttons?: React.ReactNode;
    isSelected?: boolean;
    isLoading?: boolean;
    isDisabled?: boolean;
    width?: string;
    onClick?: () => void;
}

const ArtAsset = ({ art, buttons, isSelected, isDisabled, isLoading, width, onClick }: ArtAssetProps) => {
    const [isHovered, setIsHovered] = useState(false);
    
    const handleMouseEnter = () => setIsHovered(true);
    const handleMouseLeave = () => setIsHovered(false);
    
    const handleClick = () => {
        if (!isDisabled && onClick) {
            onClick();
        }
    };
    
    // Convert SVG string to data URL
    const svgToDataUrl = (svgString: string) => {
        if (svgString.startsWith('<svg') && svgString.endsWith('</svg>')) {
            // Encode the SVG string
            const encodedSvg = encodeURIComponent(svgString);
            return `data:image/svg+xml;charset=utf-8,${encodedSvg}`;
        }
        return svgString;
    };

    const renderButtons = () => {
        if (!isHovered) return null;
        
        if (buttons) {
            return <ButtonContainer>{buttons}</ButtonContainer>;
        }
        
        return null;
    };

    return (
        <ArtAssetContainer 
            $isDisabled={isDisabled}
            $isSelected={isSelected}
            $width={width}
            onClick={handleClick}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}>
            {art.image !== '' ? (
                <div 
                    style={{ position: 'relative', width: '100%', maxWidth: '100%', aspectRatio: '1 / 1' }}
                >
                    <Image src={svgToDataUrl(art.image)} draggable="false" />
                    {isLoading && (
                        <LoadingContainer>
                            <LoadingSpinner size="medium" colorMode="dark" />
                            {/* <LoadingDots colorMode="dark"/> */}
                        </LoadingContainer>
                    )}
                    {renderButtons()}
                </div>
            ) : (
                <div 
                    style={{ position: 'relative', width: '100%', maxWidth: '100%', aspectRatio: '1 / 1' }}
                >
                    <Blank/>
                    {isLoading && (
                        <LoadingContainer>
                            <LoadingSpinner size="medium" colorMode="dark" />
                            {/* <LoadingDots colorMode="dark" /> */}
                        </LoadingContainer>
                    )}
                    {renderButtons()}
                </div>
            )}

            <Name>{art.name}</Name>
        </ArtAssetContainer>
    );
};

export default ArtAsset;